import { ChangeEvent, KeyboardEvent, MouseEvent, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { saveAuthTokens } from 'src/api/axiosClient/tokenStorage';
import { collectValidationErrors, loginValidationSchema, ValidationErrors } from 'src/constants/validations';
import { AuthContext } from 'src/contexts/AuthContext';
import { getApplicationSettings } from 'src/modules/applications/slices';
import { useAuthenticationApi } from '../api';

export const useLoginViewController = () => {
  const applicationSettings = useSelector(getApplicationSettings);

  const [formValues, setFormValues] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [showPassword, setShowPassword] = useState(false);

  const authContext = useContext(AuthContext);

  const api = useAuthenticationApi();
  const navigate = useNavigate();
  const location = useLocation();

  const signupOffered = applicationSettings?.selfService?.enabled === true;

  const handleClickShowPassword = () => {
    setShowPassword(show => !show);
  };

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const validate = async (values: typeof formValues) => {
    try {
      await loginValidationSchema.validate(values, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        setErrors(collectValidationErrors(err));
      }
      return false;
    }
  };

  const handleLogin = async () => {
    const isValid = await validate(formValues);
    if (isValid) {
      api
        .login(formValues)
        .then(({ data }) => {
          authContext?.login();
          saveAuthTokens(data);

          const from = location.state?.from;
          if (from) {
            navigate(from);
          } else {
            navigate('/home');
          }
        })
        .catch(e => {
          if (e.error === 'validation') {
            setErrors({ validation: e.message });
          } else {
            navigate('/access-denied');
          }
        });
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement | HTMLTextAreaElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleLogin();
    }
  };

  const navigateToForgotPassword = () => {
    navigate('/forgot-password');
  };

  return {
    applicationSettings,
    signupOffered,
    formValues,
    errors,
    showPassword,
    handleClickShowPassword,
    handleMouseDownPassword,
    handleChange,
    handleLogin,
    onKeyDown,
    navigateToForgotPassword,
  };
};
